/**
 * Assign a training to staff — TRUS-562.
 *
 * `POST /workforce/training-assignments` has existed since TRUS-486 and
 * `assignTraining()` has been in api.ts the whole time, called from nowhere.
 * So an admin could author a catalogue and write assignment RULES, but could
 * not hand a specific training to a specific person: every one of Amigos'
 * 159 live assignments arrived via the nightly evaluator. This is the missing
 * affordance named in TRUS-562 ("assign a training from catalog to a specific
 * staff member ... An admin should have the ability to assign/edit trainings
 * at any time").
 *
 * Two pickers, not one list, because the backend means different things by
 * them. `user_ids` is "these named people". `role_ids` is "whoever holds this
 * workforce role", resolved server-side through workforce_role_label_map at
 * the moment of assignment — so it reaches staff the admin never enumerated,
 * and the rows it creates are marked `role` rather than `individual`. An
 * explicit individual assignment outranks having been swept in by role.
 *
 * The result panel reports `already_assigned` separately from `assigned`
 * rather than collapsing both into a success count: the endpoint is idempotent
 * (ON CONFLICT DO NOTHING), so re-assigning is safe, and an admin who picks 20
 * people and sees "assigned 3" needs to know the other 17 already had it
 * rather than wondering what failed.
 */

import React, { useEffect, useMemo, useState } from 'react'
import { Search, UserPlus, Users } from 'lucide-react'
import { Button, ErrorNote, Field, INPUT, Modal, Pill } from './ui'
import {
  AssignableStaff,
  Training,
  WorkforceRole,
  assignTraining,
  listAssignableStaff,
  listWorkforceRoles,
} from './api'

interface AssignResult {
  assigned: number
  assigned_user_ids: number[]
  already_assigned_user_ids: number[]
}

export function AssignTrainingDialog({
  training,
  onClose,
  onAssigned,
}: {
  training: Training | null
  onClose: () => void
  onAssigned?: () => void
}) {
  const [staff, setStaff] = useState<AssignableStaff[]>([])
  const [roles, setRoles] = useState<WorkforceRole[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set())
  const [selectedRoles, setSelectedRoles] = useState<Set<number>>(new Set())
  const [dueDate, setDueDate] = useState('')
  const [notes, setNotes] = useState('')
  const [search, setSearch] = useState('')
  const [saving, setSaving] = useState(false)
  const [result, setResult] = useState<AssignResult | null>(null)

  const open = training !== null

  useEffect(() => {
    if (!open) return
    // Reset per opening, so the previous training's selection cannot be
    // submitted against this one.
    setSelectedUsers(new Set())
    setSelectedRoles(new Set())
    setDueDate('')
    setNotes('')
    setSearch('')
    setResult(null)
    setError(null)

    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const orgId = Number(localStorage.getItem('active_cbo_id') || 0)
        const [people, roleList] = await Promise.all([
          orgId ? listAssignableStaff(orgId) : Promise.resolve([] as AssignableStaff[]),
          // Roles are a convenience, not the dialog. An org without any
          // workforce roles configured must still be able to pick people.
          listWorkforceRoles().catch(() => ({ items: [] as WorkforceRole[], total: 0 })),
        ])
        if (cancelled) return
        setStaff(people)
        setRoles(roleList.items ?? [])
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load the staff list.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [open, training?.id])

  const visibleStaff = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return staff
    return staff.filter(
      (p) =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.email || '').toLowerCase().includes(q) ||
        (p.role_names || p.user_org_role || '').toLowerCase().includes(q),
    )
  }, [staff, search])

  const toggle = (set: Set<number>, apply: (s: Set<number>) => void, id: number) => {
    const next = new Set(set)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    apply(next)
  }

  const nothingPicked = selectedUsers.size === 0 && selectedRoles.size === 0

  const submit = async () => {
    if (!training || nothingPicked) return
    setSaving(true)
    setError(null)
    try {
      const res = await assignTraining({
        training_id: training.id,
        user_ids: selectedUsers.size ? Array.from(selectedUsers) : undefined,
        role_ids: selectedRoles.size ? Array.from(selectedRoles) : undefined,
        // An empty date input is "no explicit due date" — let the training's
        // own due rule decide — not a date of "".
        due_date: dueDate || null,
        notes: notes.trim() || null,
      })
      setResult(res)
      onAssigned?.()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not assign the training.')
    } finally {
      setSaving(false)
    }
  }

  const nameFor = (id: number) => staff.find((p) => p.id === id)?.name ?? `User ${id}`

  return (
    <Modal
      open={open}
      wide
      title={training ? `Assign: ${training.title}` : 'Assign training'}
      description={
        result
          ? undefined
          : 'Pick people, or a workforce role to reach everyone who holds it. Assigning again is safe — anyone who already has this training is left as they are.'
      }
      onClose={onClose}
      footer={
        result ? (
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => void submit()} disabled={saving || nothingPicked || loading}>
              <UserPlus className="h-4 w-4" />
              {saving ? 'Assigning…' : 'Assign'}
            </Button>
          </>
        )
      }
    >
      {error && <ErrorNote message={error} onDismiss={() => setError(null)} />}

      {result ? (
        <div className="space-y-3 text-[13px]">
          <div className="font-medium text-ink">
            {result.assigned === 0
              ? 'No new assignments were needed.'
              : `Assigned to ${result.assigned} ${result.assigned === 1 ? 'person' : 'people'}.`}
          </div>
          {result.already_assigned_user_ids.length > 0 && (
            <div className="rounded-md border border-v2border bg-paper-2 px-3 py-2 text-ink-500">
              {result.already_assigned_user_ids.length}{' '}
              {result.already_assigned_user_ids.length === 1 ? 'person already had' : 'people already had'} this
              training, and were left unchanged:{' '}
              {result.already_assigned_user_ids.map(nameFor).join(', ')}
            </div>
          )}
          {result.assigned_user_ids.length > 0 && (
            <div className="text-ink-500">{result.assigned_user_ids.map(nameFor).join(', ')}</div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {loading ? (
            <div className="py-6 text-center text-[13px] text-ink-400">Loading staff…</div>
          ) : (
            <>
              {roles.length > 0 && (
                <Field
                  label="Workforce roles"
                  hint="Reaches everyone holding the role, including staff hired later than this assignment."
                >
                  <div className="flex flex-wrap gap-1.5">
                    {roles.map((r) => {
                      const on = selectedRoles.has(r.id)
                      return (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => toggle(selectedRoles, setSelectedRoles, r.id)}
                          aria-pressed={on}
                          className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-[13px] transition-colors ${
                            on
                              ? 'border-v2accent bg-v2accent text-white'
                              : 'border-v2border bg-surface text-ink hover:bg-paper-2'
                          }`}
                        >
                          <Users className="h-3.5 w-3.5" />
                          {r.role_name}
                        </button>
                      )
                    })}
                  </div>
                </Field>
              )}

              <Field label={`Staff${selectedUsers.size ? ` (${selectedUsers.size} selected)` : ''}`}>
                <div className="relative mb-2">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
                  <input
                    className={`${INPUT} pl-8`}
                    placeholder="Search by name, email or role"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="max-h-64 overflow-y-auto rounded-md border border-v2border">
                  {visibleStaff.length === 0 ? (
                    <div className="px-3 py-6 text-center text-[13px] text-ink-400">
                      {staff.length === 0 ? 'No staff found for this organization.' : 'No one matches that search.'}
                    </div>
                  ) : (
                    visibleStaff.map((p) => (
                      <label
                        key={p.id}
                        className="flex cursor-pointer items-center gap-3 border-b border-v2border px-3 py-2 last:border-b-0 hover:bg-paper-2"
                      >
                        <input
                          type="checkbox"
                          checked={selectedUsers.has(p.id)}
                          onChange={() => toggle(selectedUsers, setSelectedUsers, p.id)}
                          className="h-4 w-4 rounded border-v2border"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[13px] text-ink">{p.name}</span>
                          <span className="block truncate text-[12px] text-ink-400">{p.email}</span>
                        </span>
                        {(p.role_names || p.user_org_role) && (
                          <Pill>{p.role_names || p.user_org_role}</Pill>
                        )}
                        {!p.last_login && <Pill tone="attention">Never signed in</Pill>}
                      </label>
                    ))
                  )}
                </div>
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Due date" hint="Leave empty to let the training's own due rule decide.">
                  <input type="date" className={INPUT} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                </Field>
                <Field label="Note" hint="Shown on the assignment. Optional.">
                  <input
                    className={INPUT}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Why this was assigned"
                  />
                </Field>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  )
}

export default AssignTrainingDialog
